/**
 * urlSanitise — B-65 security review. Some Marakin links carry single-use
 * secrets in the URL: /confirm-email/<token>, /not-me/<token>,
 * /reset-password?token=..., /:club/invite/<token>,
 * /:club/coparent-accept/<token>. B-58: what someone typed into Find your
 * club (/api/club-search?q=…) is redacted too. PostHog records the page URL on every event
 * ($current_url, $pathname, $referrer, $initial_*) and in session replay, so
 * the secrets are replaced with "[redacted]" before anything is sent
 * (lib/analytics.js before_send). Pure, no imports: tested by
 * urlSanitise.test.mjs (node --test).
 */
const PATH_SECRETS = /\/(confirm-email|not-me|invite|coparent-accept)\/[^/?#\s"'<>]+/gi;
const QUERY_SECRETS = /([?&](?:token|code|signupTicket|linkTicket)=)[^&#\s"'<>]*/gi;
const REDACTED = '[redacted]';
// B-58: the search text on a club-search URL (never logged, spec).
const SEARCH_PATH = /\/club-search(?=[/?#]|$)/i;
const SEARCH_QUERY = /([?&]q=)[^&#\s"'<>]*/gi;

export const sanitiseUrl = (value) => {
  if (typeof value !== 'string') return value;
  const search = SEARCH_PATH.test(value);
  if (!search && !/\/(confirm-email|not-me|invite|coparent-accept)\/|[?&](token|code|signupTicket|linkTicket)=/i.test(value)) return value;
  const out = value
    .replace(PATH_SECRETS, (m, kind) => `/${kind}/${REDACTED}`)
    .replace(QUERY_SECRETS, `$1${REDACTED}`);
  return search ? out.replace(SEARCH_QUERY, `$1${REDACTED}`) : out;
};

// Every string inside, at any depth (replay snapshots nest the page href).
const deepSanitise = (value, depth = 0) => {
  if (typeof value === 'string') return sanitiseUrl(value);
  if (!value || typeof value !== 'object' || depth > 40) return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map(v => { const n = deepSanitise(v, depth + 1); if (n !== v) changed = true; return n; });
    return changed ? out : value;
  }
  let changed = false;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const n = deepSanitise(v, depth + 1);
    if (n !== v) changed = true;
    out[k] = n;
  }
  return changed ? out : value;
};

/** PostHog before_send: same event, secrets removed from every URL-ish string. */
export const sanitiseEvent = (event) => {
  if (!event || typeof event !== 'object') return event;
  return {
    ...event,
    ...(event.properties ? { properties: deepSanitise(event.properties) } : {}),
    ...(event.$set ? { $set: deepSanitise(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: deepSanitise(event.$set_once) } : {}),
  };
};
